/* eslint-disable no-unused-vars */
/* jshint esversion: 11, module: true, browser: true */
// noinspection ExceptionCaughtLocallyJS,JSUnusedLocalSymbols

import { createRouter, createWebHistory } from 'vue-router';
import { sync } from 'vuex-router-sync';

import { routeToAutomatedSurveyAssignment } from '@/components/utilities/AutomatedSurveyChecker.vue';
import MiddlewareRunner from '@/middleware/MiddlewareRunner.js';
import routes from '@/router/ModularRoutes.js';
import store from '@/store/Index.js';

// The middleware for every page of the application
const globalMiddleware = [
    'CheckAuth',
    'CheckAccountDisabled',
    'ForcePasswordChange',
    'CheckQuizAttempt',
    'CheckIdentityVerificationAttempt',
    'CheckSessionTimeout',
];

// Load middleware modules dynamically
const routeMiddleware = import.meta.glob('@/middleware/*.js', { eager: true });

const PERSONAL_PROFILE_BASE_ROUTES = ['/me', '/my'];

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {import('vue-router').Route} to
 * @param  {import('vue-router').Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
        return savedPosition;
    }

    if (to.hash) {
        return { el: to.hash };
    }

    const matchedComponents = to.matched.slice().reverse();
    const [component] = matchedComponents.map(matched => matched.components.default).slice(-1);

    if (component && component.scrollToTop === false) {
        return {};
    }

    return { left: 0, top: 0 };
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior,
});

/**
 * Replace the current route and skip the middleware.
 * This is useful when you want to redirect the user to another page without
 * running the middleware.
 * @param {*[][]} args
 * @returns {Promise<NavigationFailure | void | undefined>}
 */
router.replaceAndSkipMiddleware = async function replaceAndSkipMiddleware(...args) {
    let middleware;
    let route;

    if (args.length === 2) {
        [middleware, route] = args;
    } else {
        middleware = true;
        [route] = args;
    }

    try {
        router.skipMiddleware = middleware;

        return await router.replace(route);
    } finally {
        router.skipMiddleware = false;
    }
};

/**
 * Merge the the global middleware with the components middleware.
 *
 * @param  {Array} components
 * @return {Array}
 */
async function getMiddleware(components /* Array<Function|Object> */) {
    const filteredGlobalMiddleware = globalMiddleware.filter(middlewareName => {
        if (typeof router.skipMiddleware === 'string') {
            return middlewareName !== router.skipMiddleware;
        }

        if (Array.isArray(router.skipMiddleware)) {
            return !router.skipMiddleware.includes(middlewareName);
        }

        return true;
    });

    const compiledMiddleware = [...filteredGlobalMiddleware];

    // Resolve all components concurrently. Wrap non-functions in resolved promises.
    const resolvedComponentPromises = components.map(component =>
        typeof component === 'function' ? component() : Promise.resolve({ default: component })
    );

    const resolvedComponents = await Promise.all(resolvedComponentPromises);

    // Extract middleware from the resolved components
    resolvedComponents.forEach(resolved => {
        const middleware = resolved.default?.middleware;
        if (middleware) {
            const middlewareToAdd = Array.isArray(middleware) ? middleware : [middleware];
            compiledMiddleware.push(...middlewareToAdd);
        }
    });

    return compiledMiddleware;
}

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {import('vue-router').Route} to
 * @param {import('vue-router').Route} from
 * @param {Function} next
 */
async function callMiddleware(middleware, to, from, next) {
    const stack = middleware.reverse();

    const processNextMiddleware = async wasNextCalled => {
        if (stack.length === 0 || wasNextCalled) {
            return;
        }

        const nextMiddleware = stack.pop();

        try {
            let middlewareFunction;

            if (typeof nextMiddleware === 'function') {
                middlewareFunction = nextMiddleware;
            } else {
                middlewareFunction = routeMiddleware[`/resources/js/middleware/${nextMiddleware}.js`]?.default;
            }

            if (!middlewareFunction) {
                throw new Error(`Undefined middleware [${nextMiddleware}]`);
            }

            await middlewareFunction(to, from, async (...args) => {
                if (args.length > 0) {
                    next(...args);
                } else {
                    await processNextMiddleware(false);
                }
            });
        } catch (error) {
            console.error('Middleware error:', error);
            next(false); // or handle the error as appropriate
        }
    };

    await store.dispatch('Loading/increase');

    try {
        await processNextMiddleware(false);
    } finally {
        await store.dispatch('Loading/finish');
    }
}

const _middleware = new MiddlewareRunner(store, router);

const beforeEachSetup = () => {
    return async (to, from, next) => {
        // Get the matched components and resolve them
        const components = to.matched.flatMap(routeRecord => {
            return routeRecord.components.default;
        });

        // Changing app titles to use institution name
        const appTitle = window.config?.institution?.name || window.config?.appName || 'Edverum';
        if (typeof to.meta.title === 'undefined') {
            document.title = appTitle;
        } else {
            document.title = `${to.meta.title} - ${appTitle}`;
        }

        if (components.length === 0) {
            return next();
        }

        if (router.skipMiddleware === true) {
            return next();
        }

        // Get the middleware for all the matched components.
        const componentMiddleware = await getMiddleware(components);

        let middlewareNavigationHandled = false;

        // Call each middleware
        await callMiddleware(componentMiddleware, to, from, (...args) => {
            middlewareNavigationHandled = true;
            return next(...args);
        });

        if (!middlewareNavigationHandled) {
            if (components[components.length - 1].loading !== false) {
                await store.dispatch('Loading/start');
            }

            return next();
        }
    };
};

export { beforeEachSetup };

export const afterEachSetup = _app => {
    /**
     * Global after hook.
     *
     * @param {import('vue-router').Route} to
     */
    return async function afterEach(to) {
        // ...
    };
};

sync(store, router, { moduleName: 'Route' });

export default router;

function getBasePath(route) {
    // Assuming the parent routes are one level deep
    const pathSegments = route.path.split('/');
    return pathSegments.slice(0, 2).join('/'); // This gets the first two segments, e.g., /users from /users/1/edit
}

function getBaseResource(route) {
    const pathSegments = route.path.split('/');
    const resourceType = pathSegments[1];
    const resourceId = pathSegments[2];
    if (resourceType && resourceId) {
        return { type: resourceType, id: resourceId };
    }

    return null;
}

// eslint-disable-next-line complexity
router.beforeResolve(async (to, from, next) => {
    if (store.state.isOutOfDate && !(to.path === from.path && to.hash)) {
        window.location.href = to.fullPath;

        return false;
    }

    const toBaseResource = getBaseResource(to);
    const fromBaseResource = getBaseResource(from);
    // Reset the menu if navigating away from the current base path
    // and the navigation is not solely within the profile page.
    if (
        to.path !== from.path &&
        !(
            PERSONAL_PROFILE_BASE_ROUTES.includes(getBasePath(to)) &&
            PERSONAL_PROFILE_BASE_ROUTES.includes(getBasePath(from))
        ) &&
        toBaseResource?.type !== fromBaseResource?.type &&
        toBaseResource?.id !== fromBaseResource?.id
    ) {
        await store.dispatch('Menu/reset');
    }

    const userData = store.getters['User/get'];
    const userHasActiveQuiz = store.getters['User/hasActiveQuiz'];
    const userHasQuizData = !!userData('quiz_id') && !!userData('quiz_activity');

    let quizAttempt = null;

    // noinspection UnusedCatchParameterJS
    try {
        quizAttempt = store.state.QuizAttempt[userData('quiz_id')]?.quiz_attempt;
    } catch (_error) {
        /* empty */
    }

    const { automatedSurveyAssignmentReady } = store.state;
    const isNavigatingWithinSameRoute = to.path === from.path && to.hash;

    // Don't interfere if user has an active quiz - let CheckQuizAttempt middleware handle regular quiz forcing
    if (userHasActiveQuiz) {
        return next();
    }

    // For automated surveys, check if user is ready to be forced into them
    // Only proceed if:
    // 1. There's an automated survey assignment ready
    // 2. Not navigating within same route
    // 3. User either has no quiz data OR their quiz attempt is completed
    const shouldNavigateToSurvey =
        automatedSurveyAssignmentReady && !isNavigatingWithinSameRoute && (!userHasQuizData || quizAttempt?.end_time);

    if (shouldNavigateToSurvey) {
        const assignmentRoute = await routeToAutomatedSurveyAssignment(automatedSurveyAssignmentReady);

        if (assignmentRoute.name) {
            const resolvedRoute = router.resolve(assignmentRoute);

            window.location.href = resolvedRoute?.route.fullPath;

            return false;
        }
    }

    // return middleware.run(to, from).then(() => next());
    return next();
});
